import { API_BASE_URL } from "../config/api";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import EngineerLayout from "../components/EngineerLayout";

export default function CustomerGroups() {
  const role = localStorage.getItem("role") || "";
  const location = useLocation();
  const navigate = useNavigate();
  
  const isEngineerRoute = location.pathname.startsWith("/engineer");
  const isEngineerView = role === "engineer" && isEngineerRoute;
  const canManage = role === "admin" || role === "accounts";
  
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);
  
  // Form fields
  const [groupName, setGroupName] = useState("");
  const [description, setDescription] = useState("");
  const [activeGroupId, setActiveGroupId] = useState(null);
  const [sourceGroupId, setSourceGroupId] = useState("");
  const [targetGroupId, setTargetGroupId] = useState("");
  
  const fetchGroups = async () => {
    try {
      setLoading(true);
      const res = await axios.get(API_BASE_URL + "/api/customer-groups");
      setGroups(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error("Failed to load customer groups", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (role === "engineer" && !isEngineerRoute) {
      navigate("/engineer/customer-groups", { replace: true });
    }
    fetchGroups();
  }, [role, isEngineerRoute, navigate]);

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    if (!groupName.trim()) return;
    try {
      await axios.post(API_BASE_URL + "/api/customer-groups", {
        groupName: groupName.trim(),
        description: description.trim()
      });
      alert("Customer group created successfully.");
      setShowCreateModal(false);
      setGroupName("");
      setDescription("");
      fetchGroups();
    } catch (error) {
      alert(error.response?.data?.message || "Failed to create customer group");
    }
  };

  const handleRenameGroup = async (e) => {
    e.preventDefault();
    if (!groupName.trim() || !activeGroupId) return;
    try {
      await axios.put(API_BASE_URL + `/api/customer-groups/${activeGroupId}`, {
        groupName: groupName.trim(),
        description: description.trim()
      });
      alert("Customer group updated successfully.");
      setShowRenameModal(false);
      setGroupName("");
      setDescription("");
      setActiveGroupId(null);
      fetchGroups();
    } catch (error) {
      alert(error.response?.data?.message || "Failed to update customer group");
    }
  };

  const handleMergeGroups = async (e) => {
    e.preventDefault();
    if (!sourceGroupId || !targetGroupId) {
      alert("Please select both source and target groups.");
      return;
    }
    if (sourceGroupId === targetGroupId) {
      alert("Source and target groups cannot be the same.");
      return;
    }
    
    const sourceGroup = groups.find(g => g._id === sourceGroupId);
    const targetGroup = groups.find(g => g._id === targetGroupId);
    if (!window.confirm(`Are you sure you want to merge all customers from "${sourceGroup?.groupName}" into "${targetGroup?.groupName}"? This will delete "${sourceGroup?.groupName}".`)) {
      return;
    }

    try {
      await axios.post(API_BASE_URL + "/api/customer-groups/merge", {
        sourceGroupId,
        targetGroupId
      });
      alert("Groups merged successfully.");
      setShowMergeModal(false);
      setSourceGroupId("");
      setTargetGroupId("");
      fetchGroups();
    } catch (error) {
      alert(error.response?.data?.message || "Failed to merge customer groups");
    }
  };

  const handleDeleteGroup = async (id, name, customerCount) => {
    if (customerCount > 0) {
      alert("Cannot delete a customer group that has active customer members. Move or merge them first.");
      return;
    }
    if (!window.confirm(`Are you sure you want to delete the empty group "${name}"?`)) {
      return;
    }
    try {
      await axios.delete(API_BASE_URL + `/api/customer-groups/${id}`);
      alert("Group deleted successfully.");
      fetchGroups();
    } catch (error) {
      alert(error.response?.data?.message || "Failed to delete group");
    }
  };

  const filteredGroups = groups.filter((g) =>
    g.groupName.toLowerCase().includes(search.toLowerCase())
  );

  const getProfilePath = (id) => {
    if (isEngineerView) return `/engineer/customer-groups/${id}`;
    if (isAccountsView) return `/accounts/customer-groups/${id}`;
    return `/customer-groups/${id}`;
  };

  const isAccountsView = role === "accounts";

  const content = (
    <div className="min-h-screen bg-slate-50 pb-12">
      {/* Header section */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-b border-slate-200 bg-white px-4 py-5 shadow-sm sm:px-6 lg:px-8">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-blue-700">Customer Management</p>
          <h1 className="mt-1 text-2xl font-black tracking-tight text-slate-950 sm:text-3xl">Customer Groups</h1>
          <p className="mt-1 text-sm text-slate-500">Group multiple accounts for unified reporting and aggregated stats.</p>
        </div>
        {canManage && (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => {
                setSourceGroupId("");
                setTargetGroupId("");
                setShowMergeModal(true);
              }}
              className="inline-flex items-center justify-center rounded-[6px] border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm font-bold text-blue-700 shadow-sm transition hover:bg-blue-100"
            >
              Merge Groups
            </button>
            <button
              onClick={() => {
                setGroupName("");
                setDescription("");
                setShowCreateModal(true);
              }}
              className="inline-flex items-center justify-center rounded-[6px] bg-blue-600 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-blue-700"
            >
              + Create Group
            </button>
          </div>
        )}
      </div>

      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        {/* Search */}
        <div className="mb-6 max-w-md">
          <div className="relative">
            <input
              type="text"
              placeholder="Search groups..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-10 w-full rounded-[6px] border border-slate-200 bg-white pl-10 pr-4 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            />
            <span className="material-symbols-outlined absolute left-3 top-2.5 text-slate-400 text-lg">search</span>
          </div>
        </div>

        {/* Table/Cards */}
        {loading ? (
          <div className="py-12 text-center text-sm text-slate-500">Loading customer groups...</div>
        ) : filteredGroups.length === 0 ? (
          <div className="rounded-lg border border-slate-200 bg-white py-12 text-center text-sm text-slate-500">
            No customer groups found.
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-left text-sm text-slate-700">
                <thead className="bg-slate-50 text-xs font-bold uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-6 py-4">Group Name</th>
                    <th className="px-6 py-4">Description</th>
                    <th className="px-6 py-4 text-center">Customers</th>
                    <th className="px-6 py-4 text-center">Open Complaints</th>
                    <th className="px-6 py-4 text-center">Completed Complaints</th>
                    <th className="px-6 py-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 bg-white">
                  {filteredGroups.map((group) => (
                    <tr key={group._id} className="hover:bg-slate-50/50 transition">
                      <td className="px-6 py-4 font-bold text-slate-950">
                        <button
                          onClick={() => navigate(getProfilePath(group._id))}
                          className="hover:text-blue-700 hover:underline text-left text-blue-600 font-bold"
                        >
                          {group.groupName}
                        </button>
                      </td>
                      <td className="px-6 py-4 text-slate-500 max-w-xs truncate">{group.description || "-"}</td>
                      <td className="px-6 py-4 text-center font-semibold text-slate-800">{group.customerCount || 0}</td>
                      <td className="px-6 py-4 text-center">
                        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                          (group.openComplaintsCount || 0) > 0 ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-600"
                        }`}>
                          {group.openComplaintsCount || 0}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">
                          {group.completedComplaintsCount || 0}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => navigate(getProfilePath(group._id))}
                            className="rounded-[4px] px-2.5 py-1 text-xs font-semibold text-blue-600 hover:bg-blue-50 transition"
                          >
                            View Profile
                          </button>
                          {canManage && (
                            <>
                              <button
                                onClick={() => {
                                  setActiveGroupId(group._id);
                                  setGroupName(group.groupName);
                                  setDescription(group.description || "");
                                  setShowRenameModal(true);
                                }}
                                className="rounded-[4px] px-2.5 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-100 transition"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteGroup(group._id, group.groupName, group.customerCount || 0)}
                                className={`rounded-[4px] px-2.5 py-1 text-xs font-semibold transition ${
                                  (group.customerCount || 0) > 0
                                    ? "text-slate-300 cursor-not-allowed"
                                    : "text-red-600 hover:bg-red-50"
                                }`}
                                disabled={(group.customerCount || 0) > 0}
                                title={(group.customerCount || 0) > 0 ? "Cannot delete non-empty group" : "Delete group"}
                              >
                                Delete
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* CREATE MODAL */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl animate-in fade-in zoom-in-95 duration-200">
            <h3 className="text-lg font-bold text-slate-950">Create Customer Group</h3>
            <form onSubmit={handleCreateGroup} className="mt-4 space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Group Name *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. ELGI"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  className="h-10 w-full rounded-[6px] border border-slate-200 bg-white px-3 text-sm text-slate-800 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Description</label>
                <textarea
                  placeholder="Optional details or group scope..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full rounded-[6px] border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 min-h-[80px]"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="rounded-[6px] border border-slate-200 px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-[6px] bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700"
                >
                  Create Group
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* RENAME/EDIT MODAL */}
      {showRenameModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl animate-in fade-in zoom-in-95 duration-200">
            <h3 className="text-lg font-bold text-slate-950">Edit Customer Group</h3>
            <form onSubmit={handleRenameGroup} className="mt-4 space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Group Name *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. ELGI"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  className="h-10 w-full rounded-[6px] border border-slate-200 bg-white px-3 text-sm text-slate-800 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Description</label>
                <textarea
                  placeholder="Optional details or group scope..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full rounded-[6px] border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 min-h-[80px]"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowRenameModal(false);
                    setActiveGroupId(null);
                  }}
                  className="rounded-[6px] border border-slate-200 px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-[6px] bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MERGE MODAL */}
      {showMergeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl animate-in fade-in zoom-in-95 duration-200">
            <h3 className="text-lg font-bold text-slate-950">Merge Customer Groups</h3>
            <p className="mt-1 text-xs text-slate-500">This action merges all member customer accounts from the Source Group into the Target Group, and then deletes the empty Source Group.</p>
            <form onSubmit={handleMergeGroups} className="mt-4 space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Source Group (Will be merged and deleted)</label>
                <select
                  required
                  value={sourceGroupId}
                  onChange={(e) => setSourceGroupId(e.target.value)}
                  className="h-10 w-full rounded-[6px] border border-slate-200 bg-white px-3 text-sm text-slate-850 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                >
                  <option value="">Select source group...</option>
                  {groups.map(g => (
                    <option key={g._id} value={g._id}>{g.groupName} ({g.customerCount || 0} customers)</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Target Group (Will receive the customers)</label>
                <select
                  required
                  value={targetGroupId}
                  onChange={(e) => setTargetGroupId(e.target.value)}
                  className="h-10 w-full rounded-[6px] border border-slate-200 bg-white px-3 text-sm text-slate-850 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                >
                  <option value="">Select target group...</option>
                  {groups.filter(g => g._id !== sourceGroupId).map(g => (
                    <option key={g._id} value={g._id}>{g.groupName} ({g.customerCount || 0} customers)</option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowMergeModal(false)}
                  className="rounded-[6px] border border-slate-200 px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-[6px] bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700"
                >
                  Merge Groups
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );

  return isEngineerView ? <EngineerLayout>{content}</EngineerLayout> : content;
}
